// src/services/flights.js
//
// 🟢 FLIGHT CARDS (web) — detection, lookup, caching, and the send path.
//
// The phone's contract, which this must match exactly (ChatModal ~18216-18435):
//   • type: 'flight'
//   • text:       the FULL raw typed message, PLAINTEXT
//   • flightData: a Firestore MAP (never a JSON string), PLAINTEXT
//   • clientTempId prefixed `temp-flight-`
//   • lastMessage: "✈️ Flight Info: <raw match>"
//   • socket `new-message` with { type:'flight', flightData, flightText }
//
// 🟥 FLIGHT MESSAGES ARE NOT ENCRYPTED. Every other rich type on this client seals its
//    payload (locationPerDeviceKeys, pollPerDeviceKeys, …) but the phone writes flight
//    text and flight data in the clear, and its reader consumes them in the clear. If
//    the web sealed them, the app would render the ciphertext string as the card header.
//    So this file deliberately does no crypto — matching the app is the requirement.
//
// 🟥 A PARTIAL flightData CRASHES THE APP. MessageBubble only guards `if (!data)`; it
//    then reads data.status.toUpperCase(), data.origin.code, data.destination.code with
//    no optional chaining. So the rule here is absolute: write a COMPLETE object or
//    write NO object. `normalizeFlightData` exists to enforce that, and the placeholder
//    path omits the field entirely rather than sending half of one.

import { collection, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { relayNewMessage, BACKEND_URL } from './socket';
import { writeChatDoc } from './mediaUpload';
import { sealMembers } from './groupMembers';
import { setMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy

// ════════════════════════════════════════════════════════════════════════════
// 1. AIRLINE CODES
// ════════════════════════════════════════════════════════════════════════════
//
// 🟢 WHY AN ALLOWLIST AT ALL — this is the fix for "$20 gets flagged as a flight" AND
//    for "I keep running out of quota", which are the same bug wearing two hats.
//
//    The phone's detector is pure shape: /\b([A-Z]{3}|[A-Z]{2}|[A-Z]\d|\d[A-Z])\s?\d{1,4}\b/i
//    — case-insensitive, so "to 10", "as 5", "by 3", "SR 100" and "US 20" all match. Each
//    one fires a live aviationstack lookup that can only ever 404. The quota isn't being
//    spent on flights; it's being spent on ordinary sentences. Gating on a real airline
//    code means a message has to *actually look like a flight* before it costs a request.
//
//    The name is kept, not just the code, so a card can still say "American Airlines
//    AA123" when the lookup fails — the placeholder is informative instead of blank.
const AIRLINES = {
  // ── North America ──
  AA: 'American Airlines', DL: 'Delta Air Lines', UA: 'United Airlines', WN: 'Southwest Airlines',
  AS: 'Alaska Airlines', B6: 'JetBlue Airways', NK: 'Spirit Airlines', F9: 'Frontier Airlines',
  HA: 'Hawaiian Airlines', G4: 'Allegiant Air', SY: 'Sun Country Airlines', MX: 'Breeze Airways',
  AC: 'Air Canada', WS: 'WestJet', PD: 'Porter Airlines', TS: 'Air Transat', F8: 'Flair Airlines',
  AM: 'Aeroméxico', Y4: 'Volaris', VB: 'Viva Aerobus', '4O': 'Interjet',
  OO: 'SkyWest Airlines', YX: 'Republic Airways', MQ: 'Envoy Air', '9E': 'Endeavor Air',
  QX: 'Horizon Air', ZW: 'Air Wisconsin', C5: 'CommuteAir', UP: 'Bahamasair',
  // ── Middle East ──
  EK: 'Emirates', EY: 'Etihad Airways', QR: 'Qatar Airways', SV: 'Saudia', XY: 'flynas',
  F3: 'flyadeal', G9: 'Air Arabia', FZ: 'flydubai', WY: 'Oman Air', OV: 'SalamAir',
  GF: 'Gulf Air', KU: 'Kuwait Airways', J9: 'Jazeera Airways', ME: 'Middle East Airlines',
  RJ: 'Royal Jordanian', LY: 'El Al', MS: 'EgyptAir', IA: 'Iraqi Airways', IR: 'Iran Air',
  W5: 'Mahan Air', '6E': 'IndiGo', PC: 'Pegasus Airlines', TK: 'Turkish Airlines',
  AJ: 'AZAL Azerbaijan', J2: 'AZAL Azerbaijan Airlines', KC: 'Air Astana',
  // ── Europe ──
  BA: 'British Airways', VS: 'Virgin Atlantic', LH: 'Lufthansa', AF: 'Air France',
  KL: 'KLM', IB: 'Iberia', UX: 'Air Europa', VY: 'Vueling', FR: 'Ryanair', U2: 'easyJet',
  W6: 'Wizz Air', EW: 'Eurowings', LX: 'SWISS', OS: 'Austrian Airlines', SN: 'Brussels Airlines',
  SK: 'SAS', DY: 'Norwegian', D8: 'Norwegian Air Intl', AY: 'Finnair', TP: 'TAP Air Portugal',
  AZ: 'ITA Airways', A3: 'Aegean Airlines', OA: 'Olympic Air', LO: 'LOT Polish Airlines',
  OK: 'Czech Airlines', RO: 'TAROM', JU: 'Air Serbia', OU: 'Croatia Airlines',
  BT: 'airBaltic', FI: 'Icelandair', EI: 'Aer Lingus', KM: 'Air Malta', CY: 'Cyprus Airways',
  HV: 'Transavia', TO: 'Transavia France', TB: 'TUI fly Belgium', X3: 'TUI fly Deutschland',
  DE: 'Condor', SU: 'Aeroflot', S7: 'S7 Airlines',
  U6: 'Ural Airlines', N4: 'Nordwind Airlines', PS: 'Ukraine Intl', B2: 'Belavia',
  // ── Asia-Pacific ──
  SQ: 'Singapore Airlines', TR: 'Scoot', MH: 'Malaysia Airlines', AK: 'AirAsia',
  D7: 'AirAsia X', TG: 'Thai Airways', FD: 'Thai AirAsia', VN: 'Vietnam Airlines',
  VJ: 'VietJet Air', PR: 'Philippine Airlines', '5J': 'Cebu Pacific', GA: 'Garuda Indonesia',
  QG: 'Citilink', JT: 'Lion Air', CX: 'Cathay Pacific', HX: 'Hong Kong Airlines',
  CI: 'China Airlines', BR: 'EVA Air', JL: 'Japan Airlines', NH: 'ANA', MM: 'Peach Aviation',
  GK: 'Jetstar Japan', KE: 'Korean Air', OZ: 'Asiana Airlines', '7C': 'Jeju Air',
  LJ: 'Jin Air', TW: "T'way Air", CA: 'Air China', MU: 'China Eastern', CZ: 'China Southern',
  HU: 'Hainan Airlines', ZH: 'Shenzhen Airlines', '3U': 'Sichuan Airlines', MF: 'Xiamen Air',
  '9C': 'Spring Airlines', QF: 'Qantas', JQ: 'Jetstar', VA: 'Virgin Australia', ZL: 'Rex Airlines',
  NZ: 'Air New Zealand', FJ: 'Fiji Airways', PX: 'Air Niugini',
  // ── South Asia ──
  AI: 'Air India', IX: 'Air India Express', SG: 'SpiceJet', UK: 'Vistara', QP: 'Akasa Air',
  PK: 'Pakistan Intl', ER: 'Serene Air', BG: 'Biman Bangladesh', UL: 'SriLankan Airlines',
  RA: 'Nepal Airlines', KB: 'Druk Air',
  // ── Africa ──
  ET: 'Ethiopian Airlines', KQ: 'Kenya Airways', SA: 'South African Airways', FA: 'FlySafair',
  '4Z': 'Airlink', AT: 'Royal Air Maroc', TU: 'Tunisair', AH: 'Air Algérie',
  W3: 'Arik Air', P4: 'Air Peace', TC: 'Air Tanzania', WB: 'RwandAir',
  // ── Latin America ──
  LA: 'LATAM Airlines', JJ: 'LATAM Brasil', G3: 'GOL', AD: 'Azul', AV: 'Avianca',
  CM: 'Copa Airlines', AR: 'Aerolíneas Argentinas', H2: 'Sky Airline', P5: 'Wingo',
  '2K': 'Avianca Ecuador', BW: 'Caribbean Airlines', JY: 'interCaribbean',
};

// ICAO (3-letter) equivalents — the server switches to `flight_icao` when the code is
// three letters, so these have to be recognised too or "UAE231" reads as noise.
const AIRLINES_ICAO = {
  AAL: 'American Airlines', DAL: 'Delta Air Lines', UAL: 'United Airlines', SWA: 'Southwest Airlines',
  ASA: 'Alaska Airlines', JBU: 'JetBlue Airways', NKS: 'Spirit Airlines', FFT: 'Frontier Airlines',
  ACA: 'Air Canada', WJA: 'WestJet', UAE: 'Emirates', ETD: 'Etihad Airways', QTR: 'Qatar Airways',
  SVA: 'Saudia', KNE: 'flynas', FAD: 'flyadeal', ABY: 'Air Arabia', FDB: 'flydubai',
  OMA: 'Oman Air', GFA: 'Gulf Air', KAC: 'Kuwait Airways', JZR: 'Jazeera Airways',
  MEA: 'Middle East Airlines', RJA: 'Royal Jordanian', ELY: 'El Al', MSR: 'EgyptAir',
  THY: 'Turkish Airlines', PGT: 'Pegasus Airlines', BAW: 'British Airways', VIR: 'Virgin Atlantic',
  DLH: 'Lufthansa', AFR: 'Air France', KLM: 'KLM', IBE: 'Iberia', RYR: 'Ryanair', EZY: 'easyJet',
  WZZ: 'Wizz Air', SWR: 'SWISS', AUA: 'Austrian Airlines', SAS: 'SAS', FIN: 'Finnair',
  TAP: 'TAP Air Portugal', AEE: 'Aegean Airlines', LOT: 'LOT Polish Airlines', ICE: 'Icelandair',
  EIN: 'Aer Lingus', SIA: 'Singapore Airlines', MAS: 'Malaysia Airlines', AXM: 'AirAsia',
  THA: 'Thai Airways', HVN: 'Vietnam Airlines', PAL: 'Philippine Airlines', GIA: 'Garuda Indonesia',
  CPA: 'Cathay Pacific', CAL: 'China Airlines', EVA: 'EVA Air', JAL: 'Japan Airlines',
  ANA: 'ANA', KAL: 'Korean Air', AAR: 'Asiana Airlines', CCA: 'Air China', CES: 'China Eastern',
  CSN: 'China Southern', QFA: 'Qantas', JST: 'Jetstar', ANZ: 'Air New Zealand',
  AIC: 'Air India', SEJ: 'SpiceJet', PIA: 'Pakistan Intl', ALK: 'SriLankan Airlines',
  ETH: 'Ethiopian Airlines', KQA: 'Kenya Airways', SAA: 'South African Airways',
  RAM: 'Royal Air Maroc', TAR: 'Tunisair', DAH: 'Air Algérie', LAN: 'LATAM Airlines',
  AVA: 'Avianca', CMP: 'Copa Airlines', GLO: 'GOL', AZU: 'Azul',
};

/** The airline's name for a code, or null if we don't know the code. */
export function airlineName(code) {
  const c = String(code || '').toUpperCase();
  return AIRLINES[c] || AIRLINES_ICAO[c] || null;
}

// ════════════════════════════════════════════════════════════════════════════
// 2. DETECTION
// ════════════════════════════════════════════════════════════════════════════

// 🟥 CURRENCY CODES ALWAYS LOSE, even when they collide with a real airline.
//    "SR 100" (Saudi riyal) and "LE 50" (Egyptian pound) are the exact shape of a
//    flight number, and "AED 500" is the exact shape of an ICAO one. Money talk is
//    vastly more common in a chat than a flight number, so a collision resolves to
//    money every time. TRY (Turkish lira) is the sharpest case — it is a currency AND
//    an English verb, and TK is Turkish Airlines' actual code, so nothing is lost.
const CURRENCY = new Set([
  'USD', 'EUR', 'GBP', 'JPY', 'CNY', 'CHF', 'CAD', 'AUD', 'NZD', 'INR', 'PKR', 'BDT',
  'SAR', 'AED', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD', 'EGP', 'LBP', 'IQD', 'YER', 'SYP',
  'TRY', 'RUB', 'UAH', 'ILS', 'ZAR', 'NGN', 'KES', 'MAD', 'TND', 'DZD', 'LYD', 'SDG',
  'MXN', 'BRL', 'ARS', 'CLP', 'COP', 'PEN', 'THB', 'MYR', 'IDR', 'PHP', 'VND', 'KRW',
  'SGD', 'HKD', 'TWD', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'ISK',
  'SR', 'LE', 'KD', 'DH', 'RS', 'BD', 'JD', 'YR', 'TL', 'RM', 'NT', 'HK', 'KR', 'FT',
]);

// 🟥 TWO-LETTER ENGLISH WORDS THAT ARE ALSO AIRLINE CODES. "at 5", "by 3", "as 5",
//    "to 10", "me 2", "on 4", "in 6", "so 2", "we 3", "or 1", "up 2" — every one of
//    these is a real IATA code somewhere. They're only accepted GLUED to the digits
//    ("AT5", never "at 5") or when the whole message is nothing but the number, which
//    is how a person actually sends a flight code and never how they write a sentence.
const AMBIGUOUS = new Set([
  'AT', 'AS', 'BY', 'TO', 'ME', 'ON', 'IN', 'IT', 'IS', 'SO', 'WE', 'OR', 'UP', 'US',
  'DO', 'GO', 'MY', 'NO', 'OF', 'AM', 'PM', 'OK', 'HI', 'ID', 'IF', 'BE', 'HE', 'AN',
  'LA', 'SA', 'CA', 'PA', 'MA', 'AD', 'AI', 'ER', 'EW', 'AR', 'AC', 'AH', 'FA', 'HA',
]);

// words that make the following number obviously not a flight
const UNIT_BEFORE = /\b(room|rooms|apt|apartment|suite|floor|seat|gate|desk|table|box|unit|page|chapter|verse|surah|no|num|number|version|ver|build|model|size|order|invoice|ticket|pin|otp|code|km|kg|lb|lbs|mb|gb|tb|kb|hz|px|usd|sar|aed|riyal|riyals|dollar|dollars|pound|pounds|euro|euros|percent)\s*$/i;
// a currency symbol or unit hard against either edge kills the match outright
const MONEY_BEFORE = /[$€£¥₹₽₺﷼#%]\s*$/;
const MONEY_AFTER = /^\s*(?:[$€£¥₹₽₺﷼%]|(?:usd|eur|gbp|sar|aed|qar|kwd|egp|jod|riyals?|dollars?|pounds?|euros?|dirhams?|percent|kg|km|lbs?|mb|gb|pm|am|hrs?|hours?|mins?|minutes?)\b)/i;
// a decimal tail means it was a quantity: "AA 12.50"
const DECIMAL_AFTER = /^[.,]\d/;

// shape only — every semantic guard is applied afterwards, against the ORIGINAL text
const SHAPE = /(?:[A-Za-z]{2,3}|[A-Za-z]\d|\d[A-Za-z])[ -]?\d{1,4}/g;
// an explicit mention overrides the allowlist: a code we've never heard of is still a
// flight if the person literally wrote the word
const FLIGHT_WORD = /(flight|flt|رحلة|الرحلة)\s*(?:number|no\.?|#)?\s*$/i;

/**
 * Find the flight number in a message.
 *
 * Differs from the phone in three deliberate ways, each one fixing a bug the user hit:
 *   1. It scans EVERY candidate, not just the first. The phone's `.match()` has no /g,
 *      so "to 10 AA100" matched "to 10", 404'd, and the real flight was never found.
 *   2. It requires a known airline code (or the literal word "flight"), which is what
 *      stops money and room numbers from burning API quota.
 *   3. It requires an ambiguous code to be glued to its digits, so ordinary English
 *      ("meet me at 5") can't look like a departure.
 *
 * @returns {{code, number, canonical, raw, airline, index}|null}
 */
export function detectFlight(input) {
  const text = String(input || '');
  if (!text.trim()) return null;
  const wholeMessage = text.trim();

  let m;
  SHAPE.lastIndex = 0;
  while ((m = SHAPE.exec(text)) !== null) {
    const raw = m[0];
    const start = m.index;
    const end = start + raw.length;

    // must not be glued to more word characters on either side ("BA123X", "XBA123")
    const prevCh = text[start - 1] || '';
    const nextCh = text[end] || '';
    if (/[A-Za-z0-9]/.test(prevCh)) continue;
    if (/[A-Za-z0-9]/.test(nextCh)) continue;

    const before = text.slice(Math.max(0, start - 24), start);
    const after = text.slice(end, end + 12);
    if (MONEY_BEFORE.test(before) || MONEY_AFTER.test(after) || DECIMAL_AFTER.test(after)) continue;
    if (UNIT_BEFORE.test(before)) continue;

    const split = /^([A-Za-z]{2,3}|[A-Za-z]\d|\d[A-Za-z])([ -]?)(\d{1,4})$/.exec(raw);
    if (!split) continue;
    const rawCode = split[1];
    const glued = split[2] === '';
    const digits = split[3];
    const code = rawCode.toUpperCase();

    if (CURRENCY.has(code)) continue;
    if (digits.length > 1 && digits[0] === '0') continue;   // no leading zeros
    if (Number(digits) === 0) continue;                      // no "AA0"

    const named = FLIGHT_WORD.test(before);
    const known = !!airlineName(code);
    if (!known && !named) continue;

    // ambiguous English words need to be glued, shouted as the entire message, or
    // introduced by the word "flight"
    if (AMBIGUOUS.has(code) && !glued && !named && wholeMessage !== raw) continue;
    // lowercase is only trusted when it's glued — "aa123" yes, "aa 123" no
    if (rawCode !== code && !glued && wholeMessage !== raw) continue;

    return {
      code,
      number: digits,
      canonical: code + digits,
      raw,
      airline: airlineName(code),
      index: start,
    };
  }
  return null;
}

// ════════════════════════════════════════════════════════════════════════════
// 3. LOOKUP — cached, deduped, quota-aware
// ════════════════════════════════════════════════════════════════════════════
//
// 🟢 THREE LAYERS OF QUOTA PROTECTION, because the user's complaint ("sometimes I run
//    out of quota") is a direct consequence of how often the phone calls this endpoint:
//      a) the allowlist above — most messages never reach the network at all
//      b) this cache, INCLUDING NEGATIVE RESULTS — a 404 is remembered for 20 minutes,
//         so re-typing the same dud number doesn't re-buy the same 404
//      c) the circuit breaker below — once the API starts refusing, we stop asking
//    Results are persisted in localStorage so a page reload doesn't reset the meter.

const CACHE_KEY = 'allo_flight_cache';
const BREAKER_KEY = 'allo_flight_breaker';
const MAX_CACHE = 120;
const TTL_MISS = 20 * 60 * 1000;
const BREAKER_MS = 10 * 60 * 1000;   // how long to stay quiet once the API looks dry
const BREAKER_STRIKES = 3;

/** Live flights go stale fast; a landed one never changes again. */
function ttlFor(data) {
  switch (data?.status) {
    case 'active': return 3 * 60 * 1000;
    case 'landed': case 'cancelled': return 12 * 60 * 60 * 1000;
    default: return 15 * 60 * 1000;
  }
}

const mem = new Map();       // canonical -> { at, ttl, data|null }
const inflight = new Map();  // canonical -> Promise
let breaker = { strikes: 0, until: 0 };

(function loadPersisted() {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    Object.entries(raw).forEach(([k, v]) => { if (v && v.at) mem.set(k, v); });
  } catch {}
  try {
    const b = JSON.parse(localStorage.getItem(BREAKER_KEY) || 'null');
    if (b && typeof b === 'object') breaker = b;
  } catch {}
}());

function persist() {
  try {
    const entries = [...mem.entries()].sort((a, b) => b[1].at - a[1].at).slice(0, MAX_CACHE);
    localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {}
}
function saveBreaker() { try { localStorage.setItem(BREAKER_KEY, JSON.stringify(breaker)); } catch {} }

/** True while we've decided the flight API is out of quota / unhealthy. */
export function flightApiDown() { return Date.now() < (breaker.until || 0); }

function noteFailure() {
  breaker.strikes = (breaker.strikes || 0) + 1;
  if (breaker.strikes >= BREAKER_STRIKES) breaker = { strikes: 0, until: Date.now() + BREAKER_MS };
  saveBreaker();
}
function noteSuccess() { if (breaker.strikes || breaker.until) { breaker = { strikes: 0, until: 0 }; saveBreaker(); } }

/** A cached result WITHOUT touching the network. Returns data, null (known miss), or undefined. */
export function cachedFlight(canonical) {
  const hit = mem.get(canonical);
  if (!hit) return undefined;
  if (Date.now() - hit.at > hit.ttl) { mem.delete(canonical); return undefined; }
  return hit.data;
}

/**
 * 🟢 Coerce whatever the API returned into the phone's EXACT shape, with every key
 *    present. MessageBubble reads data.origin.code and data.status.toUpperCase() with
 *    no guards, so an object missing one field is a crash on someone else's phone.
 *    Returns null when there isn't enough to be a real card.
 */
export function normalizeFlightData(raw, canonical) {
  if (!raw || typeof raw !== 'object') return null;
  const side = (s) => ({
    code: String(s?.code || '—').toUpperCase().slice(0, 4),
    city: String(s?.city || 'Unknown'),
    time: s?.time ? String(s.time) : '',
  });
  const status = ['scheduled', 'active', 'landed', 'cancelled'].includes(raw.status)
    ? raw.status : 'scheduled';
  const progress = Number.isFinite(Number(raw.progress))
    ? Math.max(0, Math.min(1, Number(raw.progress))) : 0;
  const code = String(canonical || '').replace(/\d+$/, '');
  const out = {
    flightNumber: String(raw.flightNumber || canonical || '').toUpperCase(),
    airline: String(raw.airline || airlineName(code) || 'Unknown Airline'),
    origin: side(raw.origin),
    destination: side(raw.destination),
    status,
    progress,
  };
  // a card with neither endpoint is worse than no card — the placeholder says more
  if (out.origin.code === '—' && out.destination.code === '—') return null;
  return out;
}

/**
 * Look a flight up. Never throws.
 * @returns {Promise<{data: object|null, reason: string|null}>}
 *   reason ∈ null | 'cached-miss' | 'notfound' | 'unavailable' | 'offline' | 'breaker'
 */
export function lookupFlight(canonical) {
  const key = String(canonical || '').toUpperCase();
  if (!key) return Promise.resolve({ data: null, reason: 'notfound' });

  const cached = cachedFlight(key);
  if (cached) return Promise.resolve({ data: cached, reason: null });
  if (cached === null) return Promise.resolve({ data: null, reason: 'cached-miss' });
  if (flightApiDown()) return Promise.resolve({ data: null, reason: 'breaker' });
  if (inflight.has(key)) return inflight.get(key);

  const p = (async () => {
    // 🟢 the phone has NO timeout on this call, so a hanging request leaves the
    //    composer spinner up forever. 8s is generous for a proxied REST hop.
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), 8000) : null;
    try {
      const res = await fetch(`${BACKEND_URL}/api/flight-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flightNumber: key }),
        signal: ctrl ? ctrl.signal : undefined,
      });
      if (!res.ok) {
        // 🟥 QUOTA IS INDISTINGUISHABLE FROM "NOT FOUND" ON THE WIRE. When aviationstack
        //    returns usage_limit_reached its body has no `data` key, and the server's
        //    `if (!json.data || !json.data.length)` turns that into the same 404 a
        //    genuinely unknown flight gets (server/functions/index.js:2255).
        //    So a 404 on a code we DO recognise is treated as "details unavailable"
        //    (→ placeholder card, message still sends) rather than "not a flight",
        //    and it counts as a strike toward the breaker.
        const known = !!airlineName(key.replace(/\d+$/, ''));
        const reason = res.status === 404 ? (known ? 'unavailable' : 'notfound') : 'unavailable';
        if (reason === 'unavailable') noteFailure();
        mem.set(key, { at: Date.now(), ttl: TTL_MISS, data: null });
        persist();
        return { data: null, reason };
      }
      const json = await res.json();
      const data = normalizeFlightData(json, key);
      if (!data) {
        mem.set(key, { at: Date.now(), ttl: TTL_MISS, data: null });
        persist();
        return { data: null, reason: 'unavailable' };
      }
      noteSuccess();
      mem.set(key, { at: Date.now(), ttl: ttlFor(data), data });
      persist();
      return { data, reason: null };
    } catch {
      // network down / aborted — do NOT cache; the next try should be allowed
      noteFailure();
      return { data: null, reason: 'offline' };
    } finally {
      if (timer) clearTimeout(timer);
      inflight.delete(key);
    }
  })();

  inflight.set(key, p);
  return p;
}

/** Wait for a lookup, but only for `ms` — the request keeps going and fills the cache. */
export function lookupWithDeadline(canonical, ms) {
  return Promise.race([
    lookupFlight(canonical),
    new Promise((r) => { setTimeout(() => r({ data: null, reason: 'slow' }), ms); }),
  ]);
}

/** Force a fresh lookup (the Retry button on a placeholder card). */
export function refreshFlight(canonical) {
  const key = String(canonical || '').toUpperCase();
  mem.delete(key);
  breaker = { strikes: 0, until: 0 }; saveBreaker();   // an explicit tap overrides the breaker
  return lookupFlight(key);
}

// ════════════════════════════════════════════════════════════════════════════
// 4. FORMATTING
// ════════════════════════════════════════════════════════════════════════════

/**
 * 🟢 AIRPORT-LOCAL TIME, NOT BROWSER TIME — a deliberate improvement on the phone.
 *    aviationstack returns each side already in ITS OWN local time with the offset
 *    attached ("2026-08-13T08:15:00+03:00"). The phone runs that through
 *    `new Date(iso).toLocaleTimeString()`, which converts it to the *viewer's* zone —
 *    so a Jeddah departure at 08:15 reads as 01:15 to someone in Detroit, which is a
 *    time that appears on no boarding pass anywhere. Reading the wall clock straight
 *    out of the string is both simpler and correct.
 */
function parseParts(iso) {
  const s = String(iso || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
  if (!m) return null;
  return {
    y: +m[1], mo: +m[2], d: +m[3], hh: +m[4], mm: +m[5],
    ms: Date.parse(s),                    // offset-aware — for durations and progress
  };
}

const use12h = (() => {
  try { return /AM|PM/i.test(new Date(2020, 0, 1, 13).toLocaleTimeString()); } catch { return false; }
})();

/** "08:15" or "1:15 PM", in the airport's own local time. */
export function flightTime(iso) {
  const p = parseParts(iso);
  if (!p) return '--:--';
  if (!use12h) return `${String(p.hh).padStart(2, '0')}:${String(p.mm).padStart(2, '0')}`;
  const h12 = p.hh % 12 === 0 ? 12 : p.hh % 12;
  return `${h12}:${String(p.mm).padStart(2, '0')} ${p.hh < 12 ? 'AM' : 'PM'}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** "Wed 13 Aug" in the airport's own local date. */
export function flightDate(iso) {
  const p = parseParts(iso);
  if (!p) return '';
  const dt = new Date(Date.UTC(p.y, p.mo - 1, p.d));
  return `${DAYS[dt.getUTCDay()]} ${p.d} ${MONTHS[p.mo - 1]}`;
}

/** "2h 27m" between the two legs, or ''. */
export function flightDuration(data) {
  const a = parseParts(data?.origin?.time);
  const b = parseParts(data?.destination?.time);
  if (!a || !b || !Number.isFinite(a.ms) || !Number.isFinite(b.ms)) return '';
  const mins = Math.round((b.ms - a.ms) / 60000);
  if (mins <= 0 || mins > 60 * 24) return '';
  const h = Math.floor(mins / 60);
  return h ? `${h}h ${String(mins % 60).padStart(2, '0')}m` : `${mins}m`;
}

/** True when the two legs land on different calendar days (so the card flags "+1"). */
export function crossesDays(data) {
  const a = parseParts(data?.origin?.time);
  const b = parseParts(data?.destination?.time);
  if (!a || !b) return false;
  return a.y !== b.y || a.mo !== b.mo || a.d !== b.d;
}

/**
 * 🟢 Progress recomputed from the clock, not read from the doc. `progress` was frozen
 *    at the moment the message was sent — an hour later the plane on a stored card is
 *    still sitting where it was. Derived from the times it stays honest, and falls back
 *    to the stored value when the times are unusable.
 */
export function flightProgress(data) {
  if (data?.status === 'landed') return 1;
  if (data?.status !== 'active') return Number(data?.progress) || 0;
  const a = parseParts(data?.origin?.time);
  const b = parseParts(data?.destination?.time);
  if (a && b && Number.isFinite(a.ms) && Number.isFinite(b.ms) && b.ms > a.ms) {
    return Math.max(0, Math.min(1, (Date.now() - a.ms) / (b.ms - a.ms)));
  }
  return Number(data?.progress) || 0;
}

export const STATUS_COLOR = {
  active: '#22C55E',     // the app's #4CAF50, nudged for contrast in dark mode
  landed: '#3B82F6',
  cancelled: '#F04438',
  delayed: '#F59E0B',
  scheduled: '#F5A524',
};
export const statusColor = (s) => STATUS_COLOR[s] || STATUS_COLOR.scheduled;
export const statusLabel = (s) => ({
  active: 'In the air', landed: 'Landed', cancelled: 'Cancelled',
  delayed: 'Delayed', scheduled: 'Scheduled',
}[s] || 'Scheduled');

/** "DTW → LAX", or '' — used by the chat list. */
export function routeLabel(data) {
  const a = data?.origin?.code;
  const b = data?.destination?.code;
  return a && b && a !== '—' && b !== '—' ? `${a} → ${b}` : '';
}

/** The flight number a message is about, wherever it ended up. */
export function flightNumberOf(m) {
  return (
    m?.flightData?.flightNumber ||
    m?.flightNumber ||
    (detectFlight(m?.text || '') || {}).canonical ||
    null
  );
}

// ════════════════════════════════════════════════════════════════════════════
// 5. SEND
// ════════════════════════════════════════════════════════════════════════════

export const FLIGHT_PREVIEW_PREFIX = '✈️ Flight Info';

/**
 * Write a flight message. `data` may be null — that's the placeholder path.
 *
 * 🟥 WHEN THERE'S NO DATA WE OMIT `flightData` ENTIRELY rather than sending a
 *    half-filled object. Two reasons, and both matter:
 *      1. the app crashes on a partial one (see the header note);
 *      2. the app has a HEALING rule — ChatModal ~13857 and ~16327 both fire on
 *         `newMsg.flightData && !oldMsg.flightData` — so a message that arrives with
 *         no data and gains it later repairs itself into a full card. Ship a stub
 *         object and that rule never fires, freezing the stub in place for good.
 */
export async function sendFlightMessage({ chat, myUid, myName, text, detection, data, clientTempId: tempId, reply }) {
  if (!chat?.id || !detection) throw new Error('nothing to send');
  // the `temp-flight-` prefix is load-bearing: ChatModal gates receipt handling and
  // merges on `id.startsWith('temp-')`, so a different prefix breaks the app's plumbing
  const clientTempId = tempId || `temp-flight-${Date.now()}`;
  const msgRef = doc(collection(db, 'chats', chat.id, 'messages'));
  const messageId = msgRef.id;
  const body = String(text || '');
  const preview = `${FLIGHT_PREVIEW_PREFIX}: ${detection.raw}`;

  await setMessageDoc(msgRef, {
    type: 'flight',
    clientTempId,
    text: body,                       // PLAINTEXT, exactly like the phone
    ...(data ? { flightData: data } : {}),
    // our own field — ignored by the app, and the only way a placeholder message can
    // still be looked up later (by us, or by another web device opening this chat)
    flightNumber: detection.canonical,
    senderId: myUid,
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
    isSending: false,
    ...(chat.isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
    // the sealed quote — an object in Firestore, an object on the wire (the server
    // re-stringifies replyTo itself); the two *PerDeviceKeys maps must stay objects
    ...(reply || {}),
  });

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid,
    participants: sealMembers(chat), isGroup: !!chat.isGroup,
    cloudText: preview, clientMsgId: clientTempId,
    extra: {
      type: 'flight',
      // the app keys the bubble's header text off `flightText`, NOT `message`
      flightText: body,
      flightNumber: detection.canonical,
      ...(data ? { flightData: data } : {}),
      ...(chat.isGroup ? { senderName: myName || 'Web' } : {}),
      ...(reply || {}),
    },
  });

  await writeChatDoc({ chat, myUid, myName, messageId, preview });
  return messageId;
}

/** Fill in a message that was sent before its details resolved. Best effort. */
export async function patchFlightData(chatId, messageId, data) {
  if (!chatId || !messageId || !data) return false;
  try {
    await updateDoc(doc(db, 'chats', chatId, 'messages', messageId), { flightData: data });
    return true;
  } catch {
    // 🟢 expected, not an error: 1:1 message docs are deleted by the server's
    //    store-and-forward sweep once delivered. The recipient keeps whatever they
    //    already had; our own copy still renders live from the cache.
    return false;
  }
}
